import { TablePagination } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { ITkTablePaginationProps } from "../../../type/componentsType/TkTablePagination";
import TkTableContext from "../../../context/tkTableContext";
import { ITableContextData } from "../../../type/contextType/tkTableContextType";

/**
 * テーブルのページネーションコンポーネントです。
 * @param count データの総数
 * @param onPageChange ページ変更時のコールバック関数
 * @param paginList ページネーションリスト
 * @param onGetPaginList ページネーションリスト取得時のコールバック関数
 * @param defaultRowsPerPage デフォルトの1ページあたりの行数
 */
const TkTablePagination: React.FC<ITkTablePaginationProps> = (props) => {
  const {
    count = 0,
    onPageChange,
    paginList = [],
    onGetPaginList,
    defaultRowsPerPage,
    pageInProps,
    dontResetPage,
  } = props;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { tableContext, setTableContext } = useContext(TkTableContext) as {
    tableContext: ITableContextData;
    setTableContext: React.Dispatch<React.SetStateAction<ITableContextData>>;
  };

  useEffect(() => {
    if (dontResetPage) return;
    setPage(0);

    setTableContext(() => ({
      ...tableContext,
      pageInContext: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginList]);

  useEffect(() => {
    if (onGetPaginList) {
      onGetPaginList(generatePaginationArray(page, rowsPerPage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, paginList]);

  useEffect(() => {
    if (defaultRowsPerPage) {
      setRowsPerPage(defaultRowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRowsPerPage]);

  useEffect(() => {
    if (pageInProps !== undefined) {
      setPage(pageInProps);
    }
  }, [pageInProps]);

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);

    setTableContext(() => ({
      ...tableContext,
      pageInContext: newPage,
    }));
    onPageChange && onPageChange(rowsPerPage, newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    let value = parseInt(event.target.value, 10);
    if (value === count) {
      setRowsPerPage(count);
    } else {
      setRowsPerPage(value);
    }
    setPage(0);

    setTableContext(() => ({
      pageInContext: 0,
      rowsPerPageInContext: value === count ? count : value,
    }));
    onPageChange && onPageChange(value, 1);
  };

  const generatePaginationArray = (currentPage: number, perPage: number) => {
    const startIndex = currentPage * perPage;
    const endIndex = startIndex + perPage;
    const paginationArray = paginList.slice(startIndex, endIndex);
    return paginationArray;
  };

  const inRange = [5, 10, 25, count].includes(rowsPerPage);
  useEffect(() => {
    if (!inRange) {
      setRowsPerPage(count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, count]);

  return (
    <TablePagination
      showFirstButton
      showLastButton
      component="div"
      count={count}
      page={count > 0 ? Math.min(page, Math.ceil(count / rowsPerPage)) : 0}
      onPageChange={handleChangePage}
      rowsPerPage={inRange ? rowsPerPage : count}
      labelRowsPerPage="表示件数"
      rowsPerPageOptions={[5, 10, 25, { value: count, label: "ALL" }]}
      onRowsPerPageChange={handleChangeRowsPerPage}
    />
  );
};

export default TkTablePagination;
